import { IconClock, IconSearch, IconX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "../../../components/ui/EmptyState";
import { ListItem } from "../../../components/ui/ListItem";

export function SearchHistoryList({
  history,
  suggestions,
  firstSuggestionIsPrimary,
  onSelectHistory,
  onSelectSuggestion,
  onRemove,
}: {
  history: string[];
  suggestions: string[];
  firstSuggestionIsPrimary: boolean;
  onSelectHistory: (value: string) => void;
  onSelectSuggestion: (value: string, isPrimarySpecialty: boolean) => void;
  onRemove: (value: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  if (history.length === 0) {
    if (suggestions.length === 0) {
      return (
        <EmptyState
          icon={<IconClock className="size-6 text-ds-slate-700" />}
          title={t("No recent searches")}
          description={t("Your recent searches will appear here.")}
          className="pt-12"
        />
      );
    }
    return (
      <div className="flex w-full flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <h2 className="text-2xl font-semibold tracking-tight text-ds-black">
            {t("Find what you're looking for")}
          </h2>
          <p className="text-[13px] text-ds-slate-500">
            {t("Search across articles, authors, videos and Rooms")}
          </p>
        </div>
        <span className="text-[10px] font-medium uppercase tracking-[1px] text-ds-slate-500">
          {t("Try searching for")}
        </span>
        <ul className="flex w-full flex-col gap-2">
          {suggestions.map((suggestion, index) => {
            const isPrimary = index === 0 && firstSuggestionIsPrimary;
            return (
              <li key={suggestion}>
                <ListItem
                  affordance={{ kind: "action" }}
                  disabled={false}
                  selected={isPrimary}
                  onClick={() => {
                    onSelectSuggestion(suggestion, isPrimary);
                  }}
                >
                  <span className="truncate">{suggestion}</span>
                </ListItem>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-1.5">
      <div className="flex items-center gap-1.5 pb-1">
        <IconClock className="size-4 text-ds-slate-400" />
        <span className="text-xs font-semibold uppercase tracking-tight text-ds-slate-400">
          {t("Recent searches")}
        </span>
      </div>
      <ul className="flex w-full flex-col">
        {history.map((entry) => (
          <li
            key={entry}
            className="flex h-10 w-full items-center gap-3 rounded-xl px-2"
          >
            <button
              type="button"
              onClick={() => {
                onSelectHistory(entry);
              }}
              className="flex min-w-0 flex-1 items-center gap-3 text-left"
            >
              <span className="flex size-6 shrink-0 items-center justify-center rounded-lg bg-ds-mint-100">
                <IconSearch className="size-3 text-ds-mint-700" />
              </span>
              <span className="flex-1 truncate text-[13px] font-medium text-ds-black">
                {entry}
              </span>
            </button>
            <button
              type="button"
              aria-label={t("Remove {{entry}} from history", { entry })}
              onClick={() => {
                onRemove(entry);
              }}
              className="flex size-6 items-center justify-center text-ds-slate-400 hover:text-ds-black"
            >
              <IconX className="size-4" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
